import React, { createContext, useContext, useState, useCallback } from 'react';

type DebugModeContextType = {
  /** デバッグモードが有効かどうか */
  isDebugModeEnabled: boolean;
  /** デバッグモードを有効化する */
  enableDebugMode: () => void;
  /** デバッグモードを無効化する */
  disableDebugMode: () => void;
};

const DebugModeContext = createContext<DebugModeContextType | undefined>(
  undefined
);

type DebugModeProviderProps = {
  children: React.ReactNode;
};

/**
 * デバッグモードの有効化状態を管理するProvider
 * 隠しコマンド（フッターのコピーライト長押し）で有効化される
 */
export const DebugModeProvider: React.FC<DebugModeProviderProps> = ({
  children,
}) => {
  const [isDebugModeEnabled, setIsDebugModeEnabled] = useState(false);

  const enableDebugMode = useCallback(() => {
    setIsDebugModeEnabled(true);
  }, []);

  const disableDebugMode = useCallback(() => {
    setIsDebugModeEnabled(false);
  }, []);

  return (
    <DebugModeContext.Provider
      value={{ isDebugModeEnabled, enableDebugMode, disableDebugMode }}
    >
      {children}
    </DebugModeContext.Provider>
  );
};

/**
 * デバッグモードの状態を取得するフック
 */
export const useDebugMode = (): DebugModeContextType => {
  const context = useContext(DebugModeContext);
  if (context === undefined) {
    throw new Error('useDebugMode must be used within a DebugModeProvider');
  }
  return context;
};

/**
 * デバッグモードの状態を安全に取得するフック
 * Providerの外で使用しても例外を投げない（undefinedを返す）
 */
export const useDebugModeSafe = (): DebugModeContextType | undefined => {
  return useContext(DebugModeContext);
};
